<h1 mat-dialog-title>Product Payroll Benefit Categories for {{data.companyName}}</h1>
<div mat-dialog-content>
  <div class="setting-mode-panel">
    <div class="setting-mode-buttons">
      @if (canInherit()) {
        <button
          mat-stroked-button
          type="button"
          [class.active-mode]="mode === 'inherit'"
          (click)="setMode('inherit')">
          Use Parent Setting
        </button>
      }
      <button
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'none'"
        (click)="setMode('none')">
        No Categories For This Company
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'custom'"
        (click)="setMode('custom')">
        Set Custom Categories
      </button>
    </div>
    @if (mode === 'inherit' && canInherit()) {
      <p class="setting-mode-help">
        This company will use Product Payroll Benefit Categories from {{data.parentCompanyName || 'its parent company'}}.
      </p>
    }
    @if (mode === 'none') {
      <p class="setting-mode-help">
        This company will explicitly override the parent setting and have no payroll benefit categories.
      </p>
    }
    @if (mode === 'custom') {
      <p class="setting-mode-help">
        Set custom payroll benefit categories and subcategory labels for this company.
      </p>
    }
  </div>

  @if (mode !== 'none') {
    <div class="subcategory-editor-panel">
      <div class="subcategory-editor-header">
        <h2 class="subcategory-editor-title">Managed Subcategories</h2>
        <p class="subcategory-editor-copy">
          Keep the top-level tabs fixed and manage the section names that appear within each tab.
        </p>
      </div>
      @if (subcategoryEditorError) {
        <app-inlinemessage
          level="warning"
          [message]="subcategoryEditorError">
        </app-inlinemessage>
      }
      <div class="subcategory-editor-grid">
        @for (category of payrollBenefitCategoryOptions; track category) {
          <div class="subcategory-editor-card">
            <div class="subcategory-editor-card-header">
              <h3>{{getCategoryLabel(category)}}</h3>
            </div>
            @if (getSubcategoryDefinitions(category).length > 0) {
              <div class="subcategory-definition-list">
                @for (definition of getSubcategoryDefinitions(category); track definition) {
                  <div class="subcategory-definition-row">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="subcategory-definition-input">
                      <mat-label>Label</mat-label>
                      <input
                        matInput
                        [disabled]="!isCustomMode()"
                        [value]="definition.label"
                        (change)="renameSubcategory(category, definition.id, $any($event.target).value)">
                    </mat-form-field>
                    <button
                      mat-icon-button
                      type="button"
                      color="warn"
                      [disabled]="!isCustomMode() || !canDeleteSubcategory(category, definition.id)"
                      [matTooltip]="canDeleteSubcategory(category, definition.id) ? 'Delete subcategory' : 'Reassign products before deleting this subcategory'"
                      (click)="deleteSubcategory(category, definition.id)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                }
              </div>
            } @else {
              <div class="subcategory-empty-state">No custom subcategories yet.</div>
            }
            @if (isCustomMode()) {
              <div class="subcategory-add-row">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="subcategory-add-input">
                  <mat-label>Add subcategory</mat-label>
                  <input
                    matInput
                    [value]="subcategoryDraftByCategory[category]"
                    (input)="updateSubcategoryDraft(category, $any($event.target).value)"
                    (keydown.enter)="addSubcategory(category)">
                </mat-form-field>
                <button
                  mat-stroked-button
                  type="button"
                  class="subcategory-add-button"
                  (click)="addSubcategory(category)">
                  Add
                </button>
              </div>
            }
          </div>
        }
      </div>
    </div>
  }

  @if (items.length === 0) {
    <app-inlinemessage
      level="warning"
      message="No products selected for this company. Set Products first.">
    </app-inlinemessage>
  }

  @if (items.length > 0) {
    <table class="table" mat-table [dataSource]="items">
      <ng-container matColumnDef="product">
        <th mat-header-cell *matHeaderCellDef>Product</th>
        <td class="cell" mat-cell *matCellDef="let item">
          {{item.vendorName}} - {{item.productName}}
        </td>
      </ng-container>
      <ng-container matColumnDef="productId">
        <th mat-header-cell *matHeaderCellDef>Product ID</th>
        <td class="cell" mat-cell *matCellDef="let item">
          {{item.id}}
        </td>
      </ng-container>
      <ng-container matColumnDef="category">
        <th mat-header-cell *matHeaderCellDef>Payroll Benefit Category</th>
        <td class="cell" mat-cell *matCellDef="let item">
          <div class="category-controls">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="category-select-field">
              <mat-select
                [value]="getMapping(item.id)?.category || ''"
                [disabled]="!isCustomMode()"
                (selectionChange)="setCategory(item.id, $event.value)">
                <mat-option value="">None</mat-option>
                @for (category of payrollBenefitCategoryOptions; track category) {
                  <mat-option [value]="category">
                    {{getCategoryLabel(category)}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (hasSubcategoryOptions(getMapping(item.id)?.category || '')) {
              <mat-form-field
                appearance="outline"
                subscriptSizing="dynamic"
                class="subcategory-select-field">
                <mat-select
                  placeholder="Subcategory (optional)"
                  [value]="getMapping(item.id)?.subcategory || ''"
                  [disabled]="!isCustomMode()"
                  (selectionChange)="setSubcategory(item.id, $event.value)">
                  <mat-option value="">None</mat-option>
                  @for (subcategory of getSubcategoryOptions(getMapping(item.id)?.category || ''); track subcategory) {
                    <mat-option
                      [value]="subcategory">
                      {{getSubcategoryLabel(getMapping(item.id)?.category || '', subcategory)}}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
          </div>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="['product', 'productId', 'category']"></tr>
      <tr mat-row *matRowDef="let row; columns: ['product', 'productId', 'category']"></tr>
    </table>
  }
</div>
<div class="buttons" mat-dialog-actions>
  <button mat-raised-button class="donebutton" color="primary" (click)="okClicked()">Done</button>
  <button mat-button (click)="onDismiss()">Cancel</button>
</div>
